import React, { useState, useEffect, useRef } from 'react';
import { Link, useRouteMatch, useParams, useHistory } from 'react-router-dom';
import { getFlowCharts, updateFlowCharts, addFlowCharts, deleteFlowCharts } from '../../services/api/flowCharts';
import { makeFlowChart } from '../../models';
import CircleButton from '../../components/CircleButton';
import { faPlus, faX } from '@fortawesome/free-solid-svg-icons';

const FlowChart = () => {
  const [flowCharts, setFlowCharts] = useState([]);
  const [addedFlowCharts, setAddedFlowCharts] = useState([]);
  const [removedFlowChartIds, setRemovedFlowChartIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const { path, url } = useRouteMatch();
  const { flowDeviceId } = useParams();
  const [loading, setLoading] = useState(true);
  const [added, setAdded] = useState(false);
  const scrollBottomEl = useRef();
  const history = useHistory();

  useEffect(() => {
    setupFlowCharts();
  }, [flowDeviceId]);

  useEffect(() => {
    if (added) {
      scrollBottomEl.current.scrollIntoView({ behavior: 'smooth' });
      setAdded(false);
    }
  }, [added]);

  const setupFlowCharts = async () => {
    setLoading(true);
    const results = await getFlowCharts(flowDeviceId);
    setFlowCharts(results.map(r => makeFlowChart(r)));
    setRemovedFlowChartIds([]);
    setAddedFlowCharts([]);
    setLoading(false);
  };

  const cancel = async () => {
    await setupFlowCharts();
  };

  const save = async () => {
    try {
      setSaving(true);
      
      if (flowCharts.length)
        await updateFlowCharts(flowCharts);

      if (removedFlowChartIds.length)
        await deleteFlowCharts(removedFlowChartIds);

      if (addedFlowCharts.length)
        await addFlowCharts(addedFlowCharts);

      setSaving(false);
      await setupFlowCharts();
    } catch (ex) {
      console.log(ex);
    } finally {
      setSaving(false);
    }
  };

  const addFlowChart = () => {
    const currentPSI = flowCharts.map(c => c.psi).reduce((prev, current) => Math.max(prev, current), 0);
    const addedPSI = addedFlowCharts.map(c => c.psi).reduce((prev, current) => Math.max(prev, current), 0);
    const maxPsi = Math.max(currentPSI, addedPSI);
    const newPSI = maxPsi === 0 ? 10 : maxPsi + 1;
    setAddedFlowCharts([...addedFlowCharts, makeFlowChart({ flowDeviceId, psi: newPSI })]);
    setAdded(true);
  };

  const removeFlowChart = (idx) => {
    const removedFlowChart = flowCharts[idx];
    setRemovedFlowChartIds([ ...removedFlowChartIds, removedFlowChart.id ])
    setFlowCharts(flowCharts.toSpliced(idx, 1))
  };

  const removeAddedFlowChart = (idx) => {
    setAddedFlowCharts(addedFlowCharts.toSpliced(idx, 1));
  };

  const changePsi = (e) => {
    const psi = !isNaN(e.target.valueAsNumber) ? e.target.valueAsNumber : 0;
    const flowChart = makeFlowChart({ ...flowCharts[Number(e.target.dataset.idx)], psi });
    setFlowCharts(flowCharts.toSpliced(Number(e.target.dataset.idx), 1, flowChart));

  };

  const changeGpm = (e) => {
    const gpm = !isNaN(e.target.valueAsNumber) ? e.target.valueAsNumber : 0;
    const flowChart = makeFlowChart({ ...flowCharts[Number(e.target.dataset.idx)], gpm });
    setFlowCharts(flowCharts.toSpliced(Number(e.target.dataset.idx), 1, flowChart));
  };

  const changeAddedPsi = (e) => {
    const psi = !isNaN(e.target.valueAsNumber) ? e.target.valueAsNumber : 0;
    const flowChart = makeFlowChart({ ...addedFlowCharts[Number(e.target.dataset.idx)], psi });
    setAddedFlowCharts(addedFlowCharts.toSpliced(Number(e.target.dataset.idx), 1, flowChart));
  };

  const changeAddedGpm = (e) => {
    const gpm = !isNaN(e.target.valueAsNumber) ? e.target.valueAsNumber : 0;
    const flowChart = makeFlowChart({ ...addedFlowCharts[Number(e.target.dataset.idx)], gpm });
    setAddedFlowCharts(addedFlowCharts.toSpliced(Number(e.target.dataset.idx), 1, flowChart));
  };

  const rows = flowCharts.map((c, idx) => (
    <tr key={idx}>
      <td><input type="number" data-idx={idx} value={c.psi ?? ''} onChange={changePsi} /></td>
      <td><input type="number" data-idx={idx} value={c.gpm ?? ''} onChange={changeGpm} /></td>
      <td><CircleButton icon={faX} type="danger" title="Remove Flow Chart" onClick={() => removeFlowChart(idx)} /></td>
    </tr>
  ));

  const addedRows = addedFlowCharts.map((c, idx) => (
    <tr key={idx}>
      <td><input type="number" data-idx={idx} value={c.psi ?? ''} onChange={changeAddedPsi} /></td>
      <td><input autoFocus type="number" data-idx={idx} value={c.gpm ?? ''} onChange={changeAddedGpm} /></td>
      <td><CircleButton icon={faX} type="danger" title="Remove Flow Chart" onClick={() => removeAddedFlowChart(idx)} /></td>
    </tr>
  ));

  return (
    <div id="flowCharts" className="list">
      <h2>Flow Chart</h2>
      {loading ? (
        <div className="spinner"></div>
      ) : (
        <table className="table table-bordered">
          <thead>
            <tr>
              <th>PSI</th>
              <th>GPM</th>
              <th className='command-row'>
                <CircleButton icon={faPlus} type="success" title="Add Flow Chart" onClick={addFlowChart} />
              </th>
            </tr>
          </thead>
          <tbody>
            {rows}
            {addedRows}
          </tbody>
          {(flowCharts.length > 0 || addedFlowCharts.length > 0) &&
            <tfoot>
              <tr>
                <td colSpan={2}></td>
                <td className='command-row'>
                  <CircleButton icon={faPlus} type="success" title="Add Flow Chart" onClick={addFlowChart} />
                </td>
              </tr>
            </tfoot>
          }
        </table>
      )}
      <div className="form-actions">
        <button type="button" className="btn btn-secondary" onClick={cancel} disabled={saving || loading}>Cancel</button>
        <button type="button" className="btn btn-primary" onClick={save} disabled={saving || loading}>Save</button>
      </div>
      <div id='scrollBottom' ref={scrollBottomEl}></div>
    </div>
  );
};

export default FlowChart;